import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { Inject, Injectable, NgZone } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject, catchError, lastValueFrom, mergeMap, Observable, of, retry, retryWhen, tap, throwError, timeout, timer } from 'rxjs';
import { ArticleHttpTimeouts } from 'src/app/_shared/consts/article-constants';
import { GoogleKeywordPlannerResponseType } from 'src/app/_shared/models/google-keyword-planner';
import { OpenAIContextResponse, OpenAIResponse } from 'src/app/_shared/models/open-ai';
import { DamConfig } from 'src/app/_shared/models/common';
import { HelperService } from 'src/app/_shared/services/helper/helper.service';
import { StorageHelper } from 'src/app/_utilities/storage-helper';
import {
  GetArticleListRequest, GetArticleListResponse,
  GetCategoryListRequest, GetCategoryListResponse, GetArticleByIdRequest,
  GetArticleByIdResponse, SaveArticleRequest, SaveArticleResponse,
  GetCategoryByIdRequest, GetCategoryByIdResponse, SaveCategoryRequest,
  SaveCategoryResponse, GetSEOWidgetResponse, UpdateArticleStatusRequest,
  UpdateArticleStatusResponse,
  BulkActionRequest,
  BulkActionResponse,
  GetProfileListResponse,
  profileDetail,
  GetArticlePublishContentDetailRequest,
  GetArticlePublishContentDetailResponse,
  DeleteCategoryAndReassignArticlesResponse,
  DeleteCategoryAndReassignArticlesRequest,
  ScanContentRequest,
  ScanContentResponse,
  SaveFaqsRequest,
  SaveFaqsResponse,
  GetArticleFaqsRequest,
  GetArticleFaqsResponse,
  DeleteFaqsResponse,
  DeleteFaqsRequest,
  PublishFaqsResponse,
  BulkOperationFaqUrlRequest,
  BulkOperationFaqUrlResponse,
  PublishArticleFaqsRequest,
  TranslateContentRequest,
  TranslateContentResponse,
  GetLanguagePackRequest,
  GetLanguagePackResponse,
  GetTranslationStatusRequest,
  GetTranslationStatusResponse,
  GetTranslatedContentRequest,
  GetTranslatedContentResponse,
  PublishMultilingualContentRequest,
  GetTemplateResponse,
  GetAllTemplateResponse,
  GenerateQuestionsByAIRequest,
  GenerateQuestionsByAIResponse,
  GetTargetAudienceListRequest,
  GetBrandVoiceListRequest,
  GetBrandVoiceListResponse,
  GetTargetAudienceListResponse,
  GetBrandVoiceRequest,
  GetBrandVoiceResponse,
  GetTargetAudienceRequest,
  GetTargetAudienceResponse,
  CreateCustomerPortalRequest,
  CreateCustomerPortalResponse,
  DefaultBrandVoiceForProfileRequest,
  DefaultBrandVoiceForProfileResponse,
  DefaultTargetAudienceForProfileRequest,
  DefaultTargetAudienceForProfileResponse,
  SchedulePublishRequest,
  CMSSiteInfo,
  ScheduleCancelRequest,
  AddOrRemoveAsFavoriteTemplateRequest,
  AddOrRemoveAsFavoriteTemplateResponse,
  ContextAnalysisDetails,
  ContextAnalysisDetailsResponse,
  ContentLinkRecommendationRequest,
  ContentLinkRecommendationResponse,
  WebsiteScanStatusResponse,
  WebsiteScanStatusRequest,
  GetAllTemplateRequest,
  ExportPDFRequest,
  BulkMetaDataRequest,
  BulkMetaDataResponse,
  UpdateBulkMetaDataRequest,
  UpdateBulkMetaDataResponse,
  GenerateTitleDescRequest,
  GenerateTitleDescResponse,
  HtmlToDocxRequest,
  ExportExcelRequest,
  ValidateUrlRequest,
  ValidateUrlResponse,
  GetArticleWidgetStatsResponse,
  GetFiltersForMyContentResponse,
  SentimentFeedbackPopupStateResponse,
  IsWorkflowEnabledRequest,
  IsWorkflowEnabledResponse,
  GetWorkflowApproverUsersRequest,
  GetWorkflowApproverUsersResponse,
  CreateWorkflowInstanceRequest,
  CreateWorkflowInstanceResponse,
  GetDashboardDataRequest,
  GetDashboardDataResponse,
  GetWorkflowCommentsRequest,
  GetWorkflowCommentsResponse,
  UpdateWorkflowInstanceStatusRequest,
  UpdateWorkflowInstanceStatusResponse,
  DisableByProfileRequest,
  DisableByProfileResponse,
  GetWorkflowInstanceByIdResponse,
  AddCommentThreadRequest,
  AddCommentReplyRequest,
  AddCommentResponse,
  EditCommentRequest,
  EditCommentResponse,
  DeleteCommentResponse,
  ResolveCommentThreadRequest,
  ResolveCommentThreadResponse,
  GetArticleCommentsResponse,
  CommentThreadDto
} from '../../_shared/models/article';
import { TopicIdeaPayloadForAIGeneration } from 'src/app/_shared/models/TopicIdeaPayloadForAIGeneration';
import { BrandVoicePayloadForAIGenerationType } from 'src/app/_shared/models/BrandVoicePayloadForAIGeneration';
import { GenerateArticleByAIRequestModel } from 'src/app/_shared/models/GenerateArticleByAIRequestModel';
import { GenerateFaqsByArticleByAIRequestModel } from 'src/app/_shared/models/GenerateFaqsByArticleByAIRequestModel';
import { GenerateCampaignArticlesRequest, GenerateCampaignArticlesResponse } from 'src/app/_shared/models/GenerateCampaignArticlesRequest';
import { GenerateCampaignsQuestionsFromAIRequest } from 'src/app/_shared/models/GenerateCampaignsQuestionsFromAIRequest';
import { TokenService } from '../token/token.service';


@Injectable({
  providedIn: 'root',
})
export class ArticleService {
  // Private Properties  

  private headers = new HttpHeaders({ 'Content-Type': 'application/json' });
  private _storageHelper: StorageHelper;
  constructor(
    private http: HttpClient,
    private _zone: NgZone,
    private tokenService: TokenService,
    @Inject('BASE_URL') private baseUrl: string,
    private helperService: HelperService,
  ) {
    this._storageHelper = new StorageHelper();
  }

  //get article list
  public getArticleList<T>(request: GetArticleListRequest): Observable<GetArticleListResponse> {
    return this.http.post<GetArticleListResponse>(
      `${this.baseUrl}api/Article/GetArticleList`,
      JSON.stringify(request),
      { headers: this.headers }
    );
  }


  //get category list
  public getCategoryList<T>(request: GetCategoryListRequest): Observable<GetCategoryListResponse> {
    return this.http.post<GetCategoryListResponse>(
      `${this.baseUrl}api/Article/GetCategoryList`,
      JSON.stringify(request),
      { headers: this.headers }
    );
  }

  //async method which returns promise - TODO:later remove
  //get category list
  public async getCategoryListAsync<T>(request: GetCategoryListRequest): Promise<GetCategoryListResponse> {
    return await lastValueFrom(this.http.post<GetCategoryListResponse>(
      `${this.baseUrl}api/Article/GetCategoryList`,
      JSON.stringify(request),
      { headers: this.headers }
    ));
  }

  //get article by ID
  public getArticleById<T>(request: GetArticleByIdRequest): Observable<GetArticleByIdResponse> {
    return this.http.post<GetArticleByIdResponse>(
      `${this.baseUrl}api/Article/GetArticleById`,
      JSON.stringify(request),
      { headers: this.headers }
    ).pipe(
      catchError((error: HttpErrorResponse) => {
        console.log(error);
        return throwError(() => error);
      }));
  }

  //save article 
  public saveArticle<T>(request: SaveArticleRequest): Observable<SaveArticleResponse> {
    return this.http.post<SaveArticleResponse>(
      `${this.baseUrl}api/Article/SaveArticle`,
      JSON.stringify(request),
      { headers: this.headers }).pipe(
        catchError((error: HttpErrorResponse) => {
          console.log(error);
          return throwError(error);
        }));
  }

  public publishMultilingualContent<T>(request: PublishMultilingualContentRequest) {
    return this.http.post(
      `${this.baseUrl}api/cmscontent/publishMultilingualContent`,
      JSON.stringify(request),
      { headers: this.headers }).pipe(
        catchError((error: HttpErrorResponse) => {
          console.log(error);
          return throwError(error);
        }));
  }
  // Preview article
  public previewPublish<T>(request: PublishMultilingualContentRequest) {
    return this.http.post(
      `${this.baseUrl}api/cmscontent/previewPublish`,
      JSON.stringify(request),
      { headers: this.headers }).pipe(
        catchError((error: HttpErrorResponse) => {
          console.log(error);
          return throwError(error);
        }));
  }

  //save article 
  public publishContent<T>(request: SaveArticleRequest) {
    return this.http.post(
      `${this.baseUrl}api/cmscontent/publishContent`,
      JSON.stringify(request),
      { headers: this.headers }).pipe(
        catchError((error: HttpErrorResponse) => {
          console.log(error);
          return throwError(error);
        }));
  }

  public updateArticleStatus<T>(request: UpdateArticleStatusRequest) {

    return this.http.post<UpdateArticleStatusResponse>(
      `${this.baseUrl}api/Article/UpdateArticleStatus`,
      JSON.stringify(request),
      { headers: this.headers }
    );
  }

  public bulkPublish(request: BulkActionRequest): Observable<BulkActionResponse> {
    return this.http.post<BulkActionResponse>(
      `${this.baseUrl}api/cmscontent/bulkPublish`,
      JSON.stringify(request),
      { headers: this.headers }
    );
  }

  public bulkRevertToDraft(request: BulkActionRequest): Observable<BulkActionResponse> {
    return this.http.post<BulkActionResponse>(
      `${this.baseUrl}api/Article/bulkRevertToDraft`,
      JSON.stringify(request),
      { headers: this.headers }
    );
  }

  //getCategoryById
  public getCategoryById<T>(request: GetCategoryByIdRequest): Observable<GetCategoryByIdResponse> {
    return this.http.post<GetCategoryByIdResponse>(
      `${this.baseUrl}api/Article/GetCategoryById`,
      JSON.stringify(request),
      { headers: this.headers });
  }
  //save category
  public saveCategory<T>(request: SaveCategoryRequest): Observable<SaveCategoryResponse> {
    return this.http.post<SaveCategoryResponse>(
      `${this.baseUrl}api/Article/SaveCategory`,
      JSON.stringify(request),
      { headers: this.headers }
    );
  }

  public getFiltersForMyContent<T>(profileId: number): Observable<GetFiltersForMyContentResponse> {
    return this.http.post<GetFiltersForMyContentResponse>(
      `${this.baseUrl}api/Article/GetFiltersForMyContent?profileId=` + profileId,
      '',
      { headers: this.headers }
    );
  }

  //get widget stats data
  public getArticlesWidgetStats<T>(profileId: number): Observable<GetArticleWidgetStatsResponse> {
    return this.http.post<GetArticleWidgetStatsResponse>(
      `${this.baseUrl}api/Article/GetArticlesWidgetStats?profileId=` + profileId,
      '',
      { headers: this.headers }
    );
  }

  //get widget data
  public getSEOWidget<T>(profileId: number): Observable<GetSEOWidgetResponse> {
    return this.http.post<GetSEOWidgetResponse>(
      `${this.baseUrl}api/Article/GetSEOWidget?profileId=` + profileId,
      '',
      { headers: this.headers }
    );
  }

  //getProfileList
  public getProfileList<T>(): Observable<GetProfileListResponse> {

    return this.http.post<GetProfileListResponse>(
      `${this.baseUrl}api/Article/GetProfileList`,
      '',
      { headers: this.headers }
    );
  }
  //get generated keyphrases from AI fallback- TODO: Change this later
  public generateKeyphrasesFromAIFallbackContext<T>(context: string, inputTone: string, inputLanguage: string, aiEngine: string = ""): Observable<OpenAIResponse> {
    let generateArticleByAIRequestModel = new GenerateArticleByAIRequestModel();
    generateArticleByAIRequestModel.Mode = "_completion";
    generateArticleByAIRequestModel.Section = "Keyphrase Generation";
    generateArticleByAIRequestModel.FieldId = "none"
    generateArticleByAIRequestModel.InputContext = "none";
    generateArticleByAIRequestModel.InputTone = inputTone;
    generateArticleByAIRequestModel.InputLanguage = inputLanguage;
    generateArticleByAIRequestModel.Context = context;
    generateArticleByAIRequestModel.AiEngine = aiEngine;
    if ((inputTone == undefined || inputTone == null || inputTone.length === 0) && (inputLanguage == undefined || inputLanguage == null || inputLanguage.length === 0)) {
      generateArticleByAIRequestModel.InputTone = "none";
      generateArticleByAIRequestModel.InputLanguage = "none";
    }
    return this.http.post<OpenAIResponse>(
      `${this.baseUrl}api/Article/GetKeyphraseFromAIFallback`,
      JSON.stringify(generateArticleByAIRequestModel),
      { headers: this.headers }
    ).pipe(timeout(ArticleHttpTimeouts.GENERATE_KEYPHRASE_TIMEOUT),
      catchError((error: HttpErrorResponse) => {
        console.log(error);
        return throwError(() => error);
      }));
  }

  //get generated keyphrases from AI- TODO: Change this later
  public generateKeyphrasesFromContext<T>(context: string, inputTone: string, inputLanguage: string, aiEngine: string = ""): Observable<OpenAIResponse> {
    let generateArticleByAIRequestModel = new GenerateArticleByAIRequestModel();
    generateArticleByAIRequestModel.Mode = "_completion";
    generateArticleByAIRequestModel.Section = "Keyphrase Generation";
    generateArticleByAIRequestModel.FieldId = "none"
    generateArticleByAIRequestModel.InputContext = "none";
    generateArticleByAIRequestModel.InputTone = inputTone;
    generateArticleByAIRequestModel.InputLanguage = inputLanguage;
    generateArticleByAIRequestModel.Context = context;
    generateArticleByAIRequestModel.AiEngine = aiEngine;
    if ((inputTone == undefined || inputTone == null || inputTone.length === 0) && (inputLanguage == undefined || inputLanguage == null || inputLanguage.length === 0)) {
      generateArticleByAIRequestModel.InputTone = "none";
      generateArticleByAIRequestModel.InputLanguage = "none";
    }
    return this.http.post<OpenAIResponse>(
      `${this.baseUrl}api/Article/generateContentByAI`,
      JSON.stringify(generateArticleByAIRequestModel),
      { headers: this.headers }
    ).pipe(timeout(ArticleHttpTimeouts.GENERATE_KEYPHRASE_TIMEOUT),
      catchError((error: HttpErrorResponse) => {
        console.log(error);
        return throwError(() => error);
      }));
  }

  //get generated post body from AI- TODO: Change this later
  public generatePostBodyFromContext<T>(keyphrase: string, inputContext: string, inputTone: string, inputLanguage: string, aiEngine: string = ""): Observable<OpenAIContextResponse> {
    let generateArticleByAIRequestModel = new GenerateArticleByAIRequestModel();
    generateArticleByAIRequestModel.Mode = "_completion";
    generateArticleByAIRequestModel.Section = "Body";
    generateArticleByAIRequestModel.FieldId = "none"
    generateArticleByAIRequestModel.InputContext = inputContext;
    generateArticleByAIRequestModel.InputTone = inputTone;
    generateArticleByAIRequestModel.InputLanguage = inputLanguage;
    generateArticleByAIRequestModel.Context = keyphrase;
    generateArticleByAIRequestModel.AiEngine = aiEngine;
    if ((inputTone == undefined || inputTone == null || inputTone.length === 0) && (inputLanguage == undefined || inputLanguage == null || inputLanguage.length === 0)) {
      generateArticleByAIRequestModel.InputTone = "none";
      generateArticleByAIRequestModel.InputLanguage = "none";
    }
    return this.http.post<OpenAIContextResponse>(
      `${this.baseUrl}api/Article/GenerateContentByAI`,
      JSON.stringify(generateArticleByAIRequestModel),
      { headers: this.headers }
    ).pipe(timeout(ArticleHttpTimeouts.GENERATE_POSTBODY_TIMEOUT),
      catchError((error: HttpErrorResponse) => {
        console.log(error);
        return throwError(() => error);
      }));
  }

  //get generated post body from AI- TODO: Change this later
  public getKeyphraseFromGoogleKeywordsPlanner<T>(keyPhrase: string): Observable<GoogleKeywordPlannerResponseType> {
    return this.http.get<GoogleKeywordPlannerResponseType>(
      `${this.baseUrl}api/Article/GetKeyphraseFromGoogleKeywordsPlanner?keyPhrase=${keyPhrase}`,
      { responseType: "json" }
    ).pipe(timeout(ArticleHttpTimeouts.GKP_TIMEOUT),
      catchError((error: HttpErrorResponse) => {
        console.log(error);
        return throwError(() => error);
      }));
  }

  public generatePromptRecommendations(topicIdea: string): Observable<any> {
    return this.http.post<any>(
      `${this.baseUrl}api/AiService/GeneratePromptRecommendations`,
      JSON.stringify({ topicIdea }),
      { headers: this.headers }
    ).pipe(timeout(ArticleHttpTimeouts.GENERATE_KEYPHRASE_TIMEOUT),
      catchError((error: HttpErrorResponse) => {
        console.log(error);
        return throwError(() => error);
      }));
  }

  //get generated individual field from AI
  public generateIndividualFieldUsingFieldID<T>(keyphrase: string, fieldId: string, inputTone: string, inputLanguage: string, aiEngine: string = ""): Observable<OpenAIResponse> {
    let generateArticleByAIRequestModel = new GenerateArticleByAIRequestModel();
    generateArticleByAIRequestModel.Mode = "_completion";
    generateArticleByAIRequestModel.Section = "Individual Fields";
    generateArticleByAIRequestModel.FieldId = fieldId;
    generateArticleByAIRequestModel.InputContext = "none";
    generateArticleByAIRequestModel.InputTone = inputTone;
    generateArticleByAIRequestModel.InputLanguage = inputLanguage;
    generateArticleByAIRequestModel.Context = keyphrase;
    generateArticleByAIRequestModel.AiEngine = "_openAI";
    if ((inputTone == undefined || inputTone == null || inputTone.length === 0) && (inputLanguage == undefined || inputLanguage == null || inputLanguage.length === 0)) {
      generateArticleByAIRequestModel.InputTone = "none";
      generateArticleByAIRequestModel.InputLanguage = "none";
    }
    return this.http.post<OpenAIResponse>(
      `${this.baseUrl}api/Article/generateContentByAI`,
      JSON.stringify(generateArticleByAIRequestModel),
      { headers: this.headers }
    ).pipe(timeout(ArticleHttpTimeouts.GENERATE_INDIVIDUAL_FIELD_TIMEOUT),
      catchError((error: HttpErrorResponse) => {
        console.log(error);
        return throwError(() => error);
      }));
  }

  //get generated post body for TopicIdea Screen
  public generateContentByAIForTopicIdea<T>(topicIdeaPayloadForAIGeneration: TopicIdeaPayloadForAIGeneration): Observable<OpenAIContextResponse> {
    return this.http.post<OpenAIContextResponse>(
      `${this.baseUrl}api/Article/GenerateContentByAIForTopicIdea`,
      JSON.stringify(topicIdeaPayloadForAIGeneration),
      { headers: this.headers }
    ).pipe(timeout(ArticleHttpTimeouts.GENERATE_POSTBODY_FORTOPICIDEA_TIMEOUT),
      catchError((error: HttpErrorResponse) => {
        console.log(error);
        return throwError(() => error);
      }));
  }

  //get generated brand voice document from AI
  public generateBrandVoiceDocumentUsingAI<T>(brandVoicePayloadForAIGeneration: BrandVoicePayloadForAIGenerationType): Observable<OpenAIResponse> {
    return this.http.post<OpenAIResponse>(
      `${this.baseUrl}api/Article/GenerateBrandVoiceDocumentByAI`,
      JSON.stringify(brandVoicePayloadForAIGeneration),
      { headers: this.headers }
    ).pipe(timeout(ArticleHttpTimeouts.GENERATE_BRANDVOICE_DOCUMENT_TIMEOUT),
      catchError((error: HttpErrorResponse) => {
        console.log(error);
        return throwError(() => error);
      }));
  }

  private _profileList = new BehaviorSubject<profileDetail[]>(undefined);
  public get profileList(): profileDetail[] {
    return this._profileList.value;
  }
  public set profileList(obj: profileDetail[]) {
    this._profileList.next(obj);
  }
  public get profileListObjectObservable(): Observable<profileDetail[]> {
    return this._profileList.asObservable();
  }

  public getArticlePublishContentDetail<T>(request: GetArticlePublishContentDetailRequest): Observable<GetArticlePublishContentDetailResponse> {
    return this.http.post<GetArticlePublishContentDetailResponse>(
      `${this.baseUrl}api/Article/GetArticlePublishContentDetail`,
      JSON.stringify(request),
      { headers: this.headers }
    );
  }

  //delete category and reassign new category to the mapped articles
  public deleteCategoryAndReassignArticles<T>(request: DeleteCategoryAndReassignArticlesRequest): Observable<DeleteCategoryAndReassignArticlesResponse> {
    return this.http.post<DeleteCategoryAndReassignArticlesResponse>(
      `${this.baseUrl}api/Article/DeleteCategoryAndReassignArticles`,
      JSON.stringify(request),
      { headers: this.headers }
    );
  }

  private _isContentIntelligenceEnabled = new BehaviorSubject<boolean>(false);
  public get IsContentIntelligenceEnabled(): boolean {
    return this._isContentIntelligenceEnabled.value;
  }
  public set IsContentIntelligenceEnabled(obj: boolean) {
    this._isContentIntelligenceEnabled.next(obj);
  }
  public get IsContentIntelligenceEnabledObjectObservable(): Observable<boolean> {
    return this._isContentIntelligenceEnabled.asObservable();
  }

  //scan the body of article
  public ScanContent<T>(request: ScanContentRequest): Observable<ScanContentResponse> {
    return this.http.post<ScanContentResponse>(
      `${this.baseUrl}api/Article/ScanContent`,
      JSON.stringify(request),
      { headers: this.headers }
    );
  }

  //Save Faqs for an article
  public SaveFaqs<T>(request: SaveFaqsRequest): Observable<SaveFaqsResponse> {
    return this.http.post<SaveFaqsResponse>(
      `${this.baseUrl}api/Article/SaveFaqs`,
      JSON.stringify(request),
      { headers: this.headers }
    );
  }

  //Delete Faqs for an article
  public DeleteFaqs<T>(request: DeleteFaqsRequest): Observable<DeleteFaqsResponse> {
    return this.http.post<DeleteFaqsResponse>(
      `${this.baseUrl}api/Article/DeleteFaqs`,
      JSON.stringify(request),
      { headers: this.headers }
    );
  }

  //Get Faqs for an article
  public GetArticleFaqs<T>(request: GetArticleFaqsRequest): Observable<GetArticleFaqsResponse> {
    return this.http.post<GetArticleFaqsResponse>(
      `${this.baseUrl}api/Article/GetArticleFaqs`,
      JSON.stringify(request),
      { headers: this.headers }
    );
  }

  //Generate Faqs By Article from AI
  public generateFaqsByArticleUsingAI<T>(generateFaqsByArticleByAI: GenerateFaqsByArticleByAIRequestModel): Observable<OpenAIResponse> {
    return this.http.post<OpenAIResponse>(
      `${this.baseUrl}api/Article/GenerateFaqsByArticle`,
      JSON.stringify(generateFaqsByArticleByAI),
      { headers: this.headers }
    ).pipe(timeout(ArticleHttpTimeouts.GENERATE_ARTICLE_FAQS_TIMEOUT),
      catchError((error: HttpErrorResponse) => {
        console.log(error);
        return throwError(() => error);
      }));
  }


  //Publish Faqs for an article
  public PublishFaqs<T>(request: PublishArticleFaqsRequest): Observable<PublishFaqsResponse> {
    return this.http.post<PublishFaqsResponse>(
      `${this.baseUrl}api/Article/PublishFaqs`,
      JSON.stringify(request),
      { headers: this.headers }
    );
  }

  //BulkOperationFaqUrl for an article
  public BulkOperationFaqUrl<T>(request: BulkOperationFaqUrlRequest): Observable<BulkOperationFaqUrlResponse> {
    return this.http.post<BulkOperationFaqUrlResponse>(
      `${this.baseUrl}api/Article/BulkOperationFaqUrl`,
      JSON.stringify(request),
      { headers: this.headers }
    );
  }

  //get Translation Status
  public getTranslationStatus<T>(request: GetTranslationStatusRequest): Observable<GetTranslationStatusResponse> {
    return this.http.post<GetTranslationStatusResponse>(
      `${this.baseUrl}api/Article/GetTranslationStatus`,
      JSON.stringify(request),
      { headers: this.headers }
    );
  }

  //get Translated Content
  public getTranslatedContent<T>(request: GetTranslatedContentRequest): Observable<GetTranslatedContentResponse> {
    return this.http.post<GetTranslatedContentResponse>(
      `${this.baseUrl}api/Article/GetTranslatedContent`,
      JSON.stringify(request),
      { headers: this.headers }
    );
  }

  //Translate Content of an article
  public TranslateContent<T>(request: TranslateContentRequest): Observable<TranslateContentResponse> {
    return this.http.post<TranslateContentResponse>(
      `${this.baseUrl}api/Article/TranslateContent`,
      JSON.stringify(request),
      { headers: this.headers }
    );
  }

  //Translate Content of an article
  public SaveArticleContentState<T>(request: SaveArticleRequest): Observable<SaveArticleResponse> {
    return this.http.post<SaveArticleResponse>(
      `${this.baseUrl}api/Article/SaveArticleContentState`,
      JSON.stringify(request),
      { headers: this.headers }
    );
  }
  // Get all Templates
  public GetTemplateList<T>(): Observable<GetTemplateResponse> {
    return this.http.post<GetTemplateResponse>(
      `${this.baseUrl}api/Article/GetTemplateList`,
      null,
      { headers: this.headers }).pipe(
        catchError((error: HttpErrorResponse) => {
          console.log(error);
          return throwError(() => error);
        }));
  }
  // Get all Templates with hierarchy
  public GetAllTemplate<T>(request: GetAllTemplateRequest): Observable<GetAllTemplateResponse> {
    return this.http.post<GetAllTemplateResponse>(
      `${this.baseUrl}api/Article/GetAllTemplate`,
      JSON.stringify(request),
      { headers: this.headers }).pipe(
        catchError((error: HttpErrorResponse) => {
          console.log(error);
          return throwError(() => error);
        }));
  }
  //Add Remove As Fav for Template
  public AddOrRemoveAsFavoriteTemplate<T>(request: AddOrRemoveAsFavoriteTemplateRequest): Observable<AddOrRemoveAsFavoriteTemplateResponse> {
    return this.http.post<AddOrRemoveAsFavoriteTemplateResponse>(
      `${this.baseUrl}api/Article/AddOrRemoveAsFavoriteTemplate`,
      JSON.stringify(request),
      { headers: this.headers }
    );
  }
  //Generate Questions from AI
  public GenerateQuestionsByAI<T>(request: GenerateQuestionsByAIRequest): Observable<GenerateQuestionsByAIResponse> {
    return this.http.post<GenerateQuestionsByAIResponse>(
      `${this.baseUrl}api/Article/GenerateQuestionsByAI`,
      JSON.stringify(request),
      { headers: this.headers }
    );
  }

  //Get Target Audience List
  public GetTargetAudienceList<T>(request: GetTargetAudienceListRequest): Observable<GetTargetAudienceListResponse> {
    return this.http.post<GetTargetAudienceListResponse>(
      `${this.baseUrl}api/Article/GetTargetAudienceList`,
      JSON.stringify(request),
      { headers: this.headers }
    );
  }
  //Get Brand Voice List
  public GetBrandVoiceList<T>(request: GetBrandVoiceListRequest): Observable<GetBrandVoiceListResponse> {
    return this.http.post<GetBrandVoiceListResponse>(
      `${this.baseUrl}api/Article/GetBrandVoiceList`,
      JSON.stringify(request),
      { headers: this.headers }
    );
  }

  //Get Target Audience 
  public GetTargetAudience<T>(request: GetTargetAudienceRequest): Observable<GetTargetAudienceResponse> {
    return this.http.post<GetTargetAudienceResponse>(
      `${this.baseUrl}api/Article/GetTargetAudience`,
      JSON.stringify(request),
      { headers: this.headers }
    );
  }

  //Get Brand Voice 
  public GetBrandVoice<T>(request: GetBrandVoiceRequest): Observable<GetBrandVoiceResponse> {
    return this.http.post<GetBrandVoiceResponse>(
      `${this.baseUrl}api/Article/GetBrandVoice`,
      JSON.stringify(request),
      { headers: this.headers }
    );
  }

  //Get Brand Voice 
  public createCustomerPortal<T>(request: CreateCustomerPortalRequest): Observable<CreateCustomerPortalResponse> {
    return this.http.post<CreateCustomerPortalResponse>(
      `${this.baseUrl}api/Article/CreateCustomerPortal`,
      JSON.stringify(request),
      { headers: this.headers }
    );
  }

  //Assign Default Target Audience For Profile
  public assignDefaultTargetAudienceForProfile<T>(request: DefaultTargetAudienceForProfileRequest): Observable<DefaultTargetAudienceForProfileResponse> {
    return this.http.post<DefaultTargetAudienceForProfileResponse>(
      `${this.baseUrl}api/Article/AssignDefaultTargetAudienceForProfile`,
      JSON.stringify(request),
      { headers: this.headers }
    );
  }

  //Assign Default Brand Voice For Profile
  public assignDefaultBrandVoiceForProfile<T>(request: DefaultBrandVoiceForProfileRequest): Observable<DefaultBrandVoiceForProfileResponse> {
    return this.http.post<DefaultBrandVoiceForProfileResponse>(
      `${this.baseUrl}api/Article/AssignDefaultBrandVoiceForProfile`,
      JSON.stringify(request),
      { headers: this.headers }
    );
  }

  //Schedule Article 
  public SchedulePublishContent<T>(request: SchedulePublishRequest) {
    return this.http.post(
      `${this.baseUrl}api/Article/SchedulePublishContent`,
      JSON.stringify(request),
      { headers: this.headers }).pipe(
        catchError((error: HttpErrorResponse) => {
          console.log(error);
          return throwError(() => error);
        }));
  }
  public GetCMSSiteInfo<T>(): Observable<CMSSiteInfo> {
    return this.http.get<CMSSiteInfo>(
      `${this.baseUrl}api/Article/GetCMSSiteInfo`,
      { responseType: "json" }).pipe(
        catchError((error: HttpErrorResponse) => {
          console.log(error);
          return throwError(() => error);
        }));
  }
  //Schedule Cancel
  public SchedulePublishCancel<T>(request: ScheduleCancelRequest) {
    return this.http.post(
      `${this.baseUrl}api/Article/SchedulePublishCancel`,
      JSON.stringify(request),
      { headers: this.headers }).pipe(
        catchError((error: HttpErrorResponse) => {
          console.log(error);
          return throwError(() => error);
        }));
  }

  public GenerateCampaignArticles<T>(request: GenerateCampaignArticlesRequest): Observable<GenerateCampaignArticlesResponse> {
    return this.http.post<GenerateCampaignArticlesResponse>(
      `${this.baseUrl}api/Campaign/GenerateCampaignArticles`,
      JSON.stringify(request),
      { headers: this.headers }
    );
  }

  public GenerateCampaignsQuestionsByAI<T>(request: GenerateCampaignsQuestionsFromAIRequest): Observable<GenerateQuestionsByAIResponse> {
    return this.http.post<GenerateQuestionsByAIResponse>(
      `${this.baseUrl}api/Campaign/GenerateCampaignsQuestionsByAI`,
      JSON.stringify(request),
      { headers: this.headers }
    );
  }

  public UploadContextAnalysisDetailsForContentRecreate<T>(request: ContextAnalysisDetails): Observable<ContextAnalysisDetailsResponse> {
    return this.http.post<ContextAnalysisDetailsResponse>(
      `${this.baseUrl}api/Article/UploadContextAnalysisDetailsForContentRecreate`,
      JSON.stringify(request),
      { headers: this.headers }
    );
  }

  public ContentLinkRecommendation<T>(request: ContentLinkRecommendationRequest): Observable<ContentLinkRecommendationResponse> {
    return this.http.post<ContentLinkRecommendationResponse>(
      `${this.baseUrl}api/Article/ContentLinkRecommendation`,
      JSON.stringify(request),
      { headers: this.headers }
    );
  }

  public WebsiteScanStatus<T>(request: WebsiteScanStatusRequest): Observable<WebsiteScanStatusResponse> {
    return this.http.post<WebsiteScanStatusResponse>(
      `${this.baseUrl}api/Article/WebsiteScanStatus`,
      JSON.stringify(request),
      { headers: this.headers }
    );
  }

  public UploadContextAnalysisDetailsForFaqGeneration<T>(request: ContextAnalysisDetails): Observable<ContextAnalysisDetailsResponse> {
    return this.http.post<ContextAnalysisDetailsResponse>(
      `${this.baseUrl}api/Article/UploadContextAnalysisDetailsForFaqGeneration`,
      JSON.stringify(request),
      { headers: this.headers }
    );
  }

  //Get BulkMetaData By ArticleId
  public GetBulkMetaData<T>(request: BulkMetaDataRequest): Observable<BulkMetaDataResponse> {
    return this.http.post<BulkMetaDataResponse>(
      `${this.baseUrl}api/Article/GetBulkMetaData`,
      JSON.stringify(request),
      { headers: this.headers });
  }
  //Update Bulk MetaData
  public UpdateBulkMetaData<T>(request: UpdateBulkMetaDataRequest): Observable<UpdateBulkMetaDataResponse> {
    return this.http.post<UpdateBulkMetaDataResponse>(
      `${this.baseUrl}api/Article/UpdateBulkMetaData`,
      JSON.stringify(request),
      { headers: this.headers }
    );
  }
  // Generate Bulk Meta Data

  public GenerateTitleDescFromAI<T>(request: GenerateTitleDescRequest): Observable<GenerateTitleDescResponse> {
    return this.http.post<GenerateTitleDescResponse>(
      `${this.baseUrl}api/Article/GenerateTitleDescFromAI`,
      JSON.stringify(request),
      { headers: this.headers }
    );
  }

  exportExcel(request: ExportExcelRequest): Observable<Blob> {
    return this.http.post(`${this.baseUrl}api/Article/ExportExcel`, request, { responseType: 'blob' });
  }

  exportPDF(request: ExportPDFRequest): Observable<Blob> {
    return this.http.post(`${this.baseUrl}api/Article/ExportPDF`, request, { responseType: 'blob' });
  }
  
  exportDocx(request: HtmlToDocxRequest): Observable<Blob> {
    return this.http.post(`${this.baseUrl}api/Article/ExportDocx`, request, { responseType: 'blob' });
  }

  public ValidateUrl(request: ValidateUrlRequest): Observable<ValidateUrlResponse> {
    return this.http.post<ValidateUrlResponse>(
      `${this.baseUrl}api/Article/ValidateUrl`,
      request,
      { headers: this.headers }
    ).pipe(
      retry({
        count: 3,
        delay: (error, retryCount) => {

          if (error instanceof HttpErrorResponse && ( error.status === 0 || error.status === 400 || error.status === 499)) {
            console.warn(`🔁 TCP error – retry ${retryCount}/3`);
            return timer(1000); 
          }
          throw error;
        }
      })
    );
  }

  private topicIdeaPayloadForAIReviseAndGeneration = new BehaviorSubject<TopicIdeaPayloadForAIGeneration>(new TopicIdeaPayloadForAIGeneration());

  // Observable the components can subscribe to
  topicIdeaPayloadForAIReviseAndGeneration$ = this.topicIdeaPayloadForAIReviseAndGeneration.asObservable();

  // Function to change state
  setTopicIdeaPayloadForAIReviseAndGeneration(value: TopicIdeaPayloadForAIGeneration) {
    this.topicIdeaPayloadForAIReviseAndGeneration.next(value);
  }

  //get generated post body for TopicIdea Screen
  public generateContentByAIForTopicIdeaStream<T>(topicIdeaPayloadForAIGeneration: TopicIdeaPayloadForAIGeneration): Observable<string> {
    const token = this.tokenService.getAccessToken();
    return new Observable<string>(observer => {
      const url = `${this.baseUrl}api/Article/GenerateContentByAIForTopicIdeaStream`;
      fetch(url, {
        method: 'POST',
        body: JSON.stringify(topicIdeaPayloadForAIGeneration),
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      })
        .then(response => {
          if (!response.body) {
            throw new Error('ReadableStream not supported or no response body.');
          }

          // A non-200 used to complete silently, which left the caller with no content and no error
          // to react to - the request looked like it never happened. Fail loudly instead.
          if (response && response.status != 200) {
            observer.error(new Error(`Content generation failed with status ${response.status}`));
            return;
          }
          const reader = response.body.getReader();
          const decoder = new TextDecoder();

          const read = () => {
            reader.read().then(({ done, value }) => {
              this._zone.run(() => {
                if (done) {
                  observer.complete();
                  return;
                }
                const chunk = decoder.decode(value, { stream: true });
                observer.next(chunk);
                read();
              });
            }).catch(error => {
              this._zone.run(() => {
                observer.error(error);
              });
            });
          };

          read();
        })
        .catch(error => {
          this._zone.run(() => {
            observer.error(error);
          });
        });

      // Teardown logic (optional, for cancellation)
      return () => {
        // No direct way to abort fetch with ReadableStream in this context unless using AbortController
      };
    });
  }

  public GetSentimentFeedbackCanDisplay(): Observable<SentimentFeedbackPopupStateResponse> {
    return this.http.get<SentimentFeedbackPopupStateResponse>(
      `${this.baseUrl}api/Article/SentimentFeedbackCanDisplay`,
      { headers: this.headers }
    );
  }

  public CloseSentimentFeedbackPopup(): Observable<SentimentFeedbackPopupStateResponse> {
    return this.http.post<SentimentFeedbackPopupStateResponse>(
      `${this.baseUrl}api/Article/SentimentFeedbackClose`,
      null,
      { headers: this.headers }
    );
  }

  // #region Milestone Workflow APIs

  //check if a workflow is enabled for a profile/entity/feature
  public IsWorkflowEnabled<T>(request: IsWorkflowEnabledRequest): Observable<IsWorkflowEnabledResponse> {
    return this.http.post<IsWorkflowEnabledResponse>(
      `${this.baseUrl}api/Article/IsWorkflowEnabled`,
      JSON.stringify(request),
      { headers: this.headers }
    );
  }

  //get the approver users for a workflow definition step
  public GetApproverUsers<T>(request: GetWorkflowApproverUsersRequest): Observable<GetWorkflowApproverUsersResponse> {
    return this.http.post<GetWorkflowApproverUsersResponse>(
      `${this.baseUrl}api/Article/GetApproverUsers`,
      JSON.stringify(request),
      { headers: this.headers }
    );
  }

  //create a new workflow instance
  public CreateWorkflowInstance<T>(request: CreateWorkflowInstanceRequest): Observable<CreateWorkflowInstanceResponse> {
    return this.http.post<CreateWorkflowInstanceResponse>(
      `${this.baseUrl}api/Article/CreateWorkflowInstance`,
      JSON.stringify(request),
      { headers: this.headers }
    );
  }

  //get the workflow dashboard data
  public GetDashboardData<T>(request: GetDashboardDataRequest): Observable<GetDashboardDataResponse> {
    return this.http.post<GetDashboardDataResponse>(
      `${this.baseUrl}api/Article/GetDashboardData`,
      JSON.stringify(request),
      { headers: this.headers }
    );
  }

  //get the comments for a workflow instance
  public GetWorkflowComments<T>(request: GetWorkflowCommentsRequest): Observable<GetWorkflowCommentsResponse> {
    return this.http.post<GetWorkflowCommentsResponse>(
      `${this.baseUrl}api/Article/GetWorkflowComments`,
      JSON.stringify(request),
      { headers: this.headers }
    );
  }

  //update the status of a workflow instance
  public UpdateWorkflowStatus<T>(request: UpdateWorkflowInstanceStatusRequest): Observable<UpdateWorkflowInstanceStatusResponse> {
    return this.http.post<UpdateWorkflowInstanceStatusResponse>(
      `${this.baseUrl}api/Article/UpdateWorkflowStatus`,
      JSON.stringify(request),
      { headers: this.headers }
    );
  }

  //disable/recall workflow instances for a profile and features
  public DisableWorkflowByProfile<T>(request: DisableByProfileRequest): Observable<DisableByProfileResponse> {
    return this.http.post<DisableByProfileResponse>(
      `${this.baseUrl}api/Article/DisableWorkflowByProfile`,
      JSON.stringify(request),
      { headers: this.headers }
    );
  }

  //get a workflow instance by its identifier
  public GetWorkflowInstanceById<T>(workflowInstanceId: number): Observable<GetWorkflowInstanceByIdResponse> {
    return this.http.get<GetWorkflowInstanceByIdResponse>(
      `${this.baseUrl}api/Article/GetWorkflowInstanceById?workflowInstanceId=${workflowInstanceId}`,
      { headers: this.headers }
    );
  }

  // #endregion

  // #region Comment Thread APIs

  //add a new comment thread for an article (anchored to selected text or unanchored)
  public AddCommentThread<T>(request: AddCommentThreadRequest): Observable<AddCommentResponse> {
    return this.http.post<AddCommentResponse>(
      `${this.baseUrl}api/Article/AddCommentThread`,
      JSON.stringify(request),
      { headers: this.headers }
    ).pipe(
      catchError((error: HttpErrorResponse) => {
        console.log(error);
        return throwError(() => error);
      }));
  }

  // In-memory cache of comment threads per article, so reopening the drawer can
  // render instantly while a fresh copy is fetched in the background.
  private commentsCache = new Map<number, CommentThreadDto[]>();

  //synchronously read the cached comment threads for an article (if any)
  public getCachedComments(articleId: number): CommentThreadDto[] | undefined {
    return this.commentsCache.get(articleId);
  }

  //add a reply to an existing comment thread
  public AddCommentReply<T>(request: AddCommentReplyRequest): Observable<AddCommentResponse> {
    return this.http.post<AddCommentResponse>(
      `${this.baseUrl}api/Article/AddCommentReply`,
      JSON.stringify(request),
      { headers: this.headers }
    ).pipe(
      catchError((error: HttpErrorResponse) => {
        console.log(error);
        return throwError(() => error);
      }));
  }

  //edit an existing comment (root comment or reply) — only the author may edit
  public EditComment<T>(request: EditCommentRequest): Observable<EditCommentResponse> {
    return this.http.put<EditCommentResponse>(
      `${this.baseUrl}api/Article/EditComment`,
      JSON.stringify(request),
      { headers: this.headers }
    ).pipe(
      catchError((error: HttpErrorResponse) => {
        console.log(error);
        return throwError(() => error);
      }));
  }

  //delete an entire comment thread (root comment + replies) — author only
  public DeleteCommentThread<T>(threadId: string): Observable<DeleteCommentResponse> {
    return this.http.delete<DeleteCommentResponse>(
      `${this.baseUrl}api/Article/DeleteCommentThread/${threadId}`,
      { headers: this.headers }
    ).pipe(
      catchError((error: HttpErrorResponse) => {
        console.log(error);
        return throwError(() => error);
      }));
  }

  //delete a single reply comment — author only
  public DeleteCommentReply<T>(commentId: number): Observable<DeleteCommentResponse> {
    return this.http.delete<DeleteCommentResponse>(
      `${this.baseUrl}api/Article/DeleteCommentReply/${commentId}`,
      { headers: this.headers }
    ).pipe(
      catchError((error: HttpErrorResponse) => {
        console.log(error);
        return throwError(() => error);
      }));
  }

  //resolve a comment thread — author only
  public ResolveCommentThread<T>(request: ResolveCommentThreadRequest): Observable<ResolveCommentThreadResponse> {
    return this.http.post<ResolveCommentThreadResponse>(
      `${this.baseUrl}api/Article/ResolveCommentThread`,
      JSON.stringify(request),
      { headers: this.headers }
    ).pipe(
      catchError((error: HttpErrorResponse) => {
        console.log(error);
        return throwError(() => error);
      }));
  }

  //get all comment threads (with nested comments) for an article
  public GetComments<T>(articleId: number): Observable<GetArticleCommentsResponse> {
    return this.http.get<GetArticleCommentsResponse>(
      `${this.baseUrl}api/Article/GetComments/${articleId}`,
      { headers: this.headers }
    ).pipe(
      tap(res => {
        if (res && !res.errorCode) {
          this.commentsCache.set(articleId, res.threads || []);
        }
      }),
      catchError((error: HttpErrorResponse) => {
        console.log(error);
        return throwError(() => error);
      }));
  }

  // #endregion

}
